import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Checkbox,
	CircularProgress,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import React, { useEffect, useMemo, useRef } from 'react';

const RoyaltyPaymentTableHead = ({
	columns,
	sort,
	order,
	onRequestSort,
	includeCheckbox,
	allSelected,
	isIndeterminate,
	onToggleAll,
}) => {
	const handleSort = (property) => () => {
		const isAsc = sort === property && order === 'asc';
		onRequestSort(isAsc ? 'desc' : 'asc', property);
	};

	return (
		<TableHead>
			<TableRow>
				{includeCheckbox ? (
					<TableCell padding="checkbox">
						<Checkbox
							checked={allSelected}
							indeterminate={isIndeterminate}
							onChange={(e, checked) => onToggleAll(checked)}
						/>
					</TableCell>
				) : null}
				{columns.map((col) => (
					<TableCell key={col.id} className={`${col.widthClass || ''}`} scope="col">
						<TableSortLabel
							active={sort === col.id}
							direction={sort === col.id ? order : 'asc'}
							onClick={handleSort(col.id)}
						>
							<span className="text-md font-bold">{col.label}</span>
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
};

const RoyaltyPaymentTable = ({
	data,
	page,
	rowsPerPage,
	setPage,
	columns,
	totalResults,
	filters,
	setFilters,
	selectedIds,
	setSelectedIds,
	loading,
}) => {
	const tableContainerRef = useRef(null);

	const handleRequestSort = (dir, property) => {
		setFilters({ ...filters, dir, sort: property });
	};

	const handleChangePage = (_, newPage) => {
		setPage(newPage);
	};

	useEffect(() => {
		if (tableContainerRef.current) {
			tableContainerRef.current.scrollTop = 0;
		}
	}, [filters, page]);

	const selectableIds = useMemo(() => data.filter((r) => r.school?.id).map((r) => r.school.id), [data]);
	const allSelected = useMemo(
		() => selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id)),
		[selectableIds, selectedIds]
	);
	const isIndeterminate = useMemo(
		() => selectableIds.some((id) => selectedIds.includes(id)) && !allSelected,
		[selectableIds, selectedIds, allSelected]
	);

	const toggleAll = (checked) => {
		setSelectedIds((prev) => {
			if (checked) {
				const merged = new Set(prev);
				selectableIds.forEach((id) => merged.add(id));
				return Array.from(merged);
			}
			return prev.filter((id) => !selectableIds.includes(id));
		});
	};

	const toggleOne = (id) => {
		if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter((x) => x !== id));
		else setSelectedIds([...selectedIds, id]);
	};

	return (
		<div className={`relative ${data?.length ? 'mb-96' : ''}`}>
			<TableContainer className="max-w-full w-full relative min-h-512 bg-white" ref={tableContainerRef}>
				<Table stickyHeader>
					<RoyaltyPaymentTableHead
						includeCheckbox
						allSelected={allSelected}
						isIndeterminate={isIndeterminate}
						onToggleAll={toggleAll}
						columns={columns}
						onRequestSort={handleRequestSort}
						order={filters.dir}
						sort={filters.sort}
					/>
					<TableBody>
						{data?.map((row) => {
							const id = row.school?.id;
							if (!id) return null;
							const isChecked = selectedIds.includes(id);
							return (
								<TableRow key={id} hover>
									<TableCell padding="checkbox">
										<Checkbox checked={isChecked} onChange={() => toggleOne(id)} />
									</TableCell>
									{columns.map((column) => (
										<TableCell key={column.id} className="truncate">
											{column.render(row)}
										</TableCell>
									))}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
				{!filters.year || !filters.quarter ? (
					<div className="flex justify-center items-center h-full  z-99 absolute w-full">
						<p>Please select a Year and Quarter</p>
					</div>
				) : !data?.length || loading ? (
					<div className="bg-black/25 justify-center items-center h-full  z-99 absolute w-full">
						<div
							className="absolute"
							style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
						>
							{loading ? <CircularProgress /> : !loading && !data?.length ? <p>No data found</p> : null}
						</div>
					</div>
				) : null}
			</TableContainer>
			<div className="flex justify-center sticky bottom-0 left-0 bg-white right-0">
				{data?.length ? (
					<Pagination
						color="primary"
						count={Math.max(1, Math.ceil((totalResults || 0) / (rowsPerPage || 20)))}
						onChange={handleChangePage}
						page={page}
					/>
				) : null}
			</div>
		</div>
	);
};

export default RoyaltyPaymentTable;
